/**
 * COPIAR SEMANA — FASE 6.7-B2
 *
 *     Semana visible (origen) → elegir destino → [ ] copiar asignaciones
 *     → PREVIEW → revisar/desmarcar filas CREAR → Confirmar → aplicar.
 *
 * UI != autoridad: el frontend no manda eventos ni clasifica nada. El
 * backend decide qué hay en la semana origen, qué se puede crear y cómo
 * (manual, manual basada en plan, materializar ocurrencia del plan) —
 * aquí sólo se pinta y se deja elegir entre las filas CREAR. Aplicar
 * revalida todo y es todo-o-nada.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';

import {
  api,
  type FilaCopiaSemana,
  type ResultadoAplicarCopiaSemana,
  type ResultadoCopia,
  type ResultadoPreviewCopiaSemana,
} from '../api.ts';
import { Error as ErrorCaja, Modal, useEsMovil } from './base.tsx';

function sumarDias(fecha: string, n: number): string {
  return new Date(Date.parse(`${fecha}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
}
export function lunesDe(fecha: string): string {
  const d = new Date(`${fecha}T00:00:00Z`).getUTCDay();
  return sumarDias(fecha, 1 - (d === 0 ? 7 : d));
}
const FORMATO_DIA = new Intl.DateTimeFormat('es-ES', { day: 'numeric', month: 'long', timeZone: 'UTC' });
const FORMATO_DIA_ANIO = new Intl.DateTimeFormat('es-ES', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
const FORMATO_CORTO = new Intl.DateTimeFormat('es-ES', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
/** "21–27 septiembre 2026" / "28 septiembre–4 octubre 2026". */
export function rangoSemana(lunes: string): string {
  const domingo = sumarDias(lunes, 6);
  const [, mesL] = lunes.split('-');
  const [, mesD] = domingo.split('-');
  const ini = FORMATO_DIA.format(new Date(`${lunes}T00:00:00Z`));
  const fin = FORMATO_DIA_ANIO.format(new Date(`${domingo}T00:00:00Z`)).replace(/ de /g, ' ');
  return mesL === mesD ? `${lunes.slice(8, 10).replace(/^0/, '')}–${fin}` : `${ini.replace(/ de /g, ' ')}–${fin}`;
}
function cuando(fecha: string, hora: string | null): string {
  return `${FORMATO_CORTO.format(new Date(`${fecha}T00:00:00Z`))}${hora !== null ? ` ${hora}` : ''}`;
}

function textoTipo(f: FilaCopiaSemana): string {
  if (f.origen.tipo === 'MANUAL') return f.origen.basadaEnPlan ? 'Manual basada en plan' : 'Manual';
  return f.origen.tipo === 'PLAN_MATERIALIZADA' ? 'Plan' : 'Proyección';
}
function textoAccion(f: FilaCopiaSemana): string {
  if (f.operacion === 'CREAR_MANUAL') return f.origen.basadaEnPlan ? 'Crear OT manual basada en plan' : 'Crear OT manual';
  if (f.operacion === 'MATERIALIZAR_PLAN') return 'Materializar ocurrencia del plan';
  return 'Ninguna';
}
const TEXTO_RESULTADO: Record<ResultadoCopia, string> = {
  CREAR: 'Crear',
  YA_EXISTE_OMITIR: 'Ya existe — se omite',
  CONFLICTO: 'Conflicto — no se crea',
  NO_COPIABLE: 'No copiable',
};
const CLASE_RESULTADO: Record<ResultadoCopia, string> = { CREAR: 'exito', YA_EXISTE_OMITIR: 'neutra', CONFLICTO: 'error', NO_COPIABLE: 'aviso' };
function etiquetaOrigen(f: FilaCopiaSemana): string {
  return f.origen.codigo ?? f.origen.codigoPlan ?? f.origen.codigoSala;
}

export function DialogoCopiarSemana({
  idCentro, semanaOrigen, puedeAsignar, cerrar, alTerminar,
}: {
  idCentro: string;
  /** Lunes de la semana visible. */
  semanaOrigen: string;
  puedeAsignar: boolean;
  cerrar: () => void;
  alTerminar: (resultado: ResultadoAplicarCopiaSemana) => void;
}): ReactNode {
  const esMovil = useEsMovil();
  const [fechaDestino, setFechaDestino] = useState(sumarDias(semanaOrigen, 7));
  const [copiarAsignaciones, setCopiarAsignaciones] = useState(false);
  const [preview, setPreview] = useState<ResultadoPreviewCopiaSemana | null>(null);
  const [seleccion, setSeleccion] = useState<ReadonlySet<string>>(new Set());
  const [cargando, setCargando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const enCursoRef = useRef(false);
  const tituloRef = useRef<HTMLHeadingElement>(null);

  const semanaDestino = /^\d{4}-\d{2}-\d{2}$/.test(fechaDestino) ? lunesDe(fechaDestino) : null;
  const mismaSemana = semanaDestino === semanaOrigen;
  useEffect(() => { if (preview !== null) tituloRef.current?.focus(); }, [preview]);

  function invalidar(): void {
    setPreview(null);
    setError(null);
  }

  async function previsualizar(): Promise<void> {
    if (semanaDestino === null || mismaSemana) return;
    setCargando(true);
    setError(null);
    try {
      const r = await api.post<ResultadoPreviewCopiaSemana>(`/api/centros/${idCentro}/planificacion/copiar-semana/preview`, {
        semanaOrigen, semanaDestino, copiarAsignaciones,
      });
      setPreview(r);
      //  Por defecto, TODAS las CREAR marcadas; el resto no es seleccionable.
      setSeleccion(new Set(r.filas.filter((f) => f.resultado === 'CREAR').map((f) => f.clave)));
    } catch (e) {
      setError(e);
    } finally {
      setCargando(false);
    }
  }

  async function confirmar(): Promise<void> {
    if (preview === null || seleccion.size === 0 || enCursoRef.current) return;
    enCursoRef.current = true;
    setEnviando(true);
    setError(null);
    try {
      const elegidas = preview.filas.filter((f) => f.resultado === 'CREAR' && seleccion.has(f.clave));
      const r = await api.post<ResultadoAplicarCopiaSemana>(`/api/centros/${idCentro}/planificacion/copiar-semana/aplicar`, {
        semanaOrigen, semanaDestino: preview.semanaDestino, copiarAsignaciones,
        seleccion: elegidas.map((f) => ({ clave: f.clave, identidadPlan: f.destino.identidadPlan })),
      });
      alTerminar(r);
    } catch (e) {
      //  Nada se aplicó (todo-o-nada): el preview visto ya no vale.
      setError(e);
      setPreview(null);
    } finally {
      enCursoRef.current = false;
      setEnviando(false);
    }
  }

  function alternar(clave: string): void {
    setSeleccion((s) => {
      const n = new Set(s);
      if (n.has(clave)) n.delete(clave); else n.add(clave);
      return n;
    });
  }

  const bloqueado = cargando || enviando;
  return (
    <Modal titulo="Copiar semana" cerrar={() => { if (!enviando) cerrar(); }}>
      {error !== null && <ErrorCaja error={error} />}
      <fieldset disabled={bloqueado} style={{ border: 0, padding: 0, margin: 0 }}>
        <legend className="sr-solo">Semanas</legend>
        <p><strong>Origen:</strong> {rangoSemana(semanaOrigen)} <span className="pequena suave">(la semana visible)</span></p>
        <div className="campo">
          <label htmlFor="copiar-destino">Semana destino (cualquier día de esa semana)</label>
          <input id="copiar-destino" type="date" value={fechaDestino} onChange={(e) => { setFechaDestino(e.target.value); invalidar(); }} />
        </div>
        {semanaDestino !== null && !mismaSemana && <p><strong>Destino:</strong> {rangoSemana(semanaDestino)}</p>}
        {mismaSemana && <p className="nota aviso" role="alert">La semana destino tiene que ser distinta de la semana origen.</p>}
        {puedeAsignar && (
          <label className="fila" style={{ gap: 6 }}>
            <input type="checkbox" checked={copiarAsignaciones} onChange={(e) => { setCopiarAsignaciones(e.target.checked); invalidar(); }} />
            Copiar asignaciones de la semana origen
          </label>
        )}
        {!copiarAsignaciones && <p className="pequena suave">No se copiarán las asignaciones de la semana origen.</p>}
        <p className="pequena suave">Nunca se sobrescribe lo que ya existe en destino, ni se modifica ningún plan.</p>
        <button type="button" className={preview === null ? 'principal' : 'secundario'} disabled={semanaDestino === null || mismaSemana} onClick={() => void previsualizar()}>
          {cargando ? 'Calculando…' : preview === null ? 'Previsualizar' : 'Volver a previsualizar'}
        </button>
      </fieldset>

      {preview !== null && (
        <section className="sep" aria-labelledby="copiar-preview-titulo">
          <h3 id="copiar-preview-titulo" ref={tituloRef} tabIndex={-1}>Previsualización</h3>
          <p className="pequena" role="status">
            {preview.resumen.total} elementos origen · {preview.resumen.crear} crear · {preview.resumen.yaExiste} ya existen · {preview.resumen.conflicto} conflicto(s) · {preview.resumen.noCopiable} no copiable(s) · {seleccion.size} seleccionado(s)
          </p>
          {preview.filas.length === 0 && <p className="suave">No hay nada que copiar en la semana origen.</p>}
          {esMovil
            ? <FilasEnTarjetas filas={preview.filas} seleccion={seleccion} alternar={alternar} bloqueado={bloqueado} />
            : <FilasEnTabla filas={preview.filas} seleccion={seleccion} alternar={alternar} bloqueado={bloqueado} />}
          {preview.conflictos.length > 0 && (
            <div className="nota aviso" style={{ marginTop: 8 }}>
              <p>Avisos en destino:</p>
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {preview.conflictos.map((a, i) => <li key={i}><span aria-hidden="true">⚠ </span>{a.mensaje}</li>)}
              </ul>
            </div>
          )}
          <div className="fila" style={{ gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
            <button type="button" className="secundario" onClick={cerrar} disabled={enviando}>Cancelar</button>
            <button type="button" className="principal" onClick={() => void confirmar()} disabled={seleccion.size === 0 || bloqueado}>
              {enviando ? 'Aplicando…' : `Confirmar (${String(seleccion.size)})`}
            </button>
          </div>
        </section>
      )}
    </Modal>
  );
}

function Seleccion({ f, seleccion, alternar, bloqueado }: { f: FilaCopiaSemana; seleccion: ReadonlySet<string>; alternar: (c: string) => void; bloqueado: boolean }): ReactNode {
  if (f.resultado !== 'CREAR') return <span className="pequena suave">No seleccionable</span>;
  return (
    <input
      type="checkbox" checked={seleccion.has(f.clave)} disabled={bloqueado} onChange={() => alternar(f.clave)}
      aria-label={`Crear ${etiquetaOrigen(f)} en ${cuando(f.destino.fecha, f.destino.hora)}`}
    />
  );
}

function Asignaciones({ f }: { f: FilaCopiaSemana }): ReactNode {
  if (f.asignaciones.length === 0) return <span className="suave">{f.resultado === 'CREAR' ? 'Sin asignación' : '—'}</span>;
  return (
    <ul style={{ margin: 0, paddingLeft: 16 }}>
      {f.asignaciones.map((a) => (
        <li key={a.codigo}>{a.nombre} — {a.procedencia === 'DEFECTO_PLAN' ? 'asignación por defecto del plan' : 'se copiaría desde la semana origen'}</li>
      ))}
    </ul>
  );
}

function Avisos({ f }: { f: FilaCopiaSemana }): ReactNode {
  const textos = [...f.warnings, ...f.alertas.map((a) => a.mensaje)];
  if (textos.length === 0) return <span className="suave">Sin avisos</span>;
  return (
    <ul style={{ margin: 0, paddingLeft: 16 }}>
      {textos.map((t, i) => <li key={i}><span aria-hidden="true">⚠ </span>{t}</li>)}
    </ul>
  );
}

function Resultado({ f }: { f: FilaCopiaSemana }): ReactNode {
  return (
    <>
      <span className={`etiqueta ${CLASE_RESULTADO[f.resultado]}`}>{TEXTO_RESULTADO[f.resultado]}</span>
      {f.motivo !== null && <div className="pequena">{f.motivo}</div>}
    </>
  );
}

function FilasEnTabla({ filas, seleccion, alternar, bloqueado }: { filas: readonly FilaCopiaSemana[]; seleccion: ReadonlySet<string>; alternar: (c: string) => void; bloqueado: boolean }): ReactNode {
  return (
    <div className="tabla-desliza">
      <table>
        <caption className="sr-solo">Resultado previsto por elemento de la semana origen</caption>
        <thead>
          <tr>
            <th scope="col">Crear</th><th scope="col">Origen</th><th scope="col">Tipo</th><th scope="col">Destino</th>
            <th scope="col">Acción prevista</th><th scope="col">Asignaciones</th><th scope="col">Avisos</th><th scope="col">Resultado</th>
          </tr>
        </thead>
        <tbody>
          {filas.map((f) => (
            <tr key={f.clave}>
              <td><Seleccion f={f} seleccion={seleccion} alternar={alternar} bloqueado={bloqueado} /></td>
              <td>{cuando(f.origen.fecha, f.origen.hora)} · <span className="mono">{etiquetaOrigen(f)}</span></td>
              <td>{textoTipo(f)}</td>
              <td>→ {cuando(f.destino.fecha, f.destino.hora)}</td>
              <td>{textoAccion(f)}</td>
              <td className="pequena"><Asignaciones f={f} /></td>
              <td className="pequena"><Avisos f={f} /></td>
              <td><Resultado f={f} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Móvil (brief §X): la tabla no cabe — una tarjeta por elemento, checkbox real a toda la fila. */
function FilasEnTarjetas({ filas, seleccion, alternar, bloqueado }: { filas: readonly FilaCopiaSemana[]; seleccion: ReadonlySet<string>; alternar: (c: string) => void; bloqueado: boolean }): ReactNode {
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {filas.map((f) => (
        <li key={f.clave} className="panel" style={{ padding: 10 }}>
          <div className="fila" style={{ gap: 8, justifyContent: 'space-between' }}>
            <strong>{cuando(f.origen.fecha, f.origen.hora)} · {etiquetaOrigen(f)}</strong>
            <Seleccion f={f} seleccion={seleccion} alternar={alternar} bloqueado={bloqueado} />
          </div>
          <div className="pequena">{textoTipo(f)} → {cuando(f.destino.fecha, f.destino.hora)} · {textoAccion(f)}</div>
          <Resultado f={f} />
          <div className="pequena"><Asignaciones f={f} /></div>
          <div className="pequena"><Avisos f={f} /></div>
        </li>
      ))}
    </ul>
  );
}
